'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogAppCTA, BlogWhatTheAppChecks, BlogSources, BlogTLDR, BlogKeyTakeaways, BlogFAQ } from '@/app/components/blog/BlogAeo'

export default function Post14Content() {
  return (
    <BlogPostLayout
      title="Microplastics in Canned Food: Linings, BPA & What Leaches (2026)"
      author="Elise Maren"
      date="July 8, 2025"
      readTime="8 min read"
      heroImage="/blog/heroes/microplastics-in-canned-food.webp"
      heroImageAlt="Opened metal food cans showing the interior polymer lining beside a glass jar alternative"
    >
      <BlogTLDR>
        <strong>Every food can has a plastic polymer lining</strong> (BPA
        epoxy or BPA-free replacements) to prevent the food touching metal.
        These linings leach microplastics and bisphenols into the food, worst when the contents are{' '}
        <strong>acidic (tomato, citrus, pickled), oily, or sat on the shelf
        for years</strong>. Glass-jarred or pouch alternatives are
        meaningfully cleaner for high-acid foods. Tetrapaks are typically
        cleaner than cans on average.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Every food can has a polymer lining, historically BPA epoxy, now mostly polyester, acrylic or vinyl.</>,
          <>Acidic foods (tomatoes, citrus, pickled items) drive the most leaching.</>,
          <>&ldquo;BPA-free&rdquo; cans do <em>not</em> simply use BPS instead, that swap happened in till receipts, not cans. But BPA-free means &ldquo;not added on purpose,&rdquo; not bisphenol-free.</>,
          <>Glass jars are cleaner for high-acid items. Pouches are similar risk to cans for some products.</>,
          <>Reduce intake of canned tomatoes, citrus, pickled vegetables, and tuna; substitute with glass-jarred or fresh equivalents.</>,
        ]}
      />

      {/* Introduction */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Hidden Plastic Lining Inside Every Can
      </h2>
      <p>
        Canned food has been a kitchen staple for over two centuries. It is affordable,
        shelf-stable, and provides convenient access to everything from tomatoes and
        beans to tuna and soup. But there is something inside those metal cans that
        most consumers never think about: a thin polymer lining that coats the interior
        of virtually every food can sold today. This lining, originally designed to
        prevent metal corrosion and food spoilage, is made of plastic, and it is a
        direct source of microplastic contamination in your diet.
      </p>
      <p>
        The most common lining material has historically been epoxy resin containing
        bisphenol A (BPA), a known endocrine disruptor. In response to consumer
        pressure, many manufacturers have shifted to BPA-free alternatives, but as
        with plastic containers, the replacements are not necessarily safer. Meanwhile,
        research is increasingly finding that these can linings release microplastic
        particles into the food they are supposed to protect, adding another layer of
        concern to an already complicated picture.
      </p>

      {/* How microplastics enter canned food */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How Microplastics Enter Canned Food
      </h2>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Can Lining Degradation
      </h3>
      <p>
        The primary source of microplastics in canned food is the polymer lining itself.
        These linings are typically made from epoxy resins, polyester, vinyl, acrylic,
        or polyolefin-based coatings. Over time, and particularly under the high-heat
        conditions of the canning process (which involves heating cans to sterilize
        their contents), the lining can degrade and release microscopic plastic particles
        into the food. Acidic foods like tomatoes and citrus accelerate this degradation
        because acids interact chemically with the polymer coating, causing it to break
        down more rapidly.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Chemical Leaching from Linings
      </h3>
      <p>
        Beyond physical microplastic particles, can linings leach chemical additives
        into food. BPA has been the most studied of these, with research linking it to
        hormone disruption, reproductive problems, cardiovascular disease, and metabolic
        disorders. In a Harvard randomised trial, volunteers who ate one serving of
        canned soup a day for five days showed urinary BPA levels{' '}
        <strong>1,221% higher</strong> than the same people eating fresh soup. That
        figure is specific to canned soup, which is a near-worst case (liquid, hot-filled,
        long contact with the whole inner surface), it is not the average for all canned
        food. But it shows how readily the lining gives up its chemistry to the contents.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        What &ldquo;BPA-free&rdquo; actually replaced BPA with
      </h3>
      <p>
        Here we have to correct something this site previously got wrong, and that most
        wellness coverage still repeats: <strong>the claim that BPA-free cans just use
        BPS or BPF instead is not supported by the evidence.</strong> When researchers
        have actually identified what is on the inside of a can (rather than assuming) they do not find BPS. The Can Manufacturers Institute fingerprinted the linings
        of 234 US cans by infrared spectroscopy in 2020 and found polyester, vinyl,
        acrylic and epoxy, with BPA epoxy in just two cans (both imported) and{' '}
        <strong>no BPS or BPF at all</strong>. The Ecology Center&apos;s{' '}
        <em>Buyer Beware</em> study tested 192 cans in 2016, as industry critics, not
        allies, and came back with the same list of replacements.
      </p>
      <p>
        The BPA-to-BPS substitution people are thinking of is real, but it happened in{' '}
        <Link href="/blog/bpa-on-thermal-receipts" className="text-[#1E6B47] hover:underline font-semibold">
          thermal receipt paper
        </Link>, where &ldquo;BPA-free&rdquo; till rolls
        genuinely are BPS-based. Somewhere along the way that fact got transplanted onto
        cans and repeated until it sounded like common knowledge.
      </p>
      <p>
        The honest picture is more awkward than either slogan. BPA-free is a{' '}
        <em>non-intent</em> claim: the industry&apos;s own wording is that it &ldquo;does
        not intentionally add BPF, BPB and BPS substitute in can linings&rdquo;, which
        says nothing about trace carryover, and indeed trace bisphenols still turn up in
        non-BPA cans. One of the most widely used replacements,{' '}
        <strong>TMBPF</strong> (a bisphenol-derived epoxy now reportedly on more than 20
        billion cans), <em>is</em> a bisphenol, and its clean safety record rests largely
        on manufacturer-funded studies. Meanwhile the other substitutes carry their own
        open questions: vinyl/organosol coatings are made from vinyl chloride, a known
        carcinogen, and the polyester oligomers now migrating into food have almost no
        independent toxicology behind them.
      </p>
      <p>
        So: BPA-free cans are <strong>probably better than BPA cans, and definitely less
        studied</strong>. That is a real improvement and a real unknown at the same time,
        and neither the alarmist version (&ldquo;it&apos;s just BPS now&rdquo;) nor the
        reassuring one (&ldquo;problem solved&rdquo;) survives contact with the data.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Pre-Existing Contamination of Contents
      </h3>
      <p>
        Some microplastics in canned food may originate before the food is even canned.
        Fish and shellfish, for example, accumulate microplastics from ocean pollution
        during their lifetimes. When these seafood products are canned, they bring their
        existing microplastic load with them. Similarly, vegetables grown in soil treated
        with biosolids (recycled sewage) can contain microplastics absorbed from the
        growing medium. For more on this topic, see our article on{' '}
        <Link href="/blog/microplastics-in-seafood" className="text-[#1E6B47] hover:underline font-medium">
          microplastics in seafood
        </Link>.
      </p>

      {/* Which canned foods are worst */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Which Canned Foods Have the Highest Microplastic Contamination?
      </h2>
      <p>
        Not all canned foods carry the same microplastic risk. Several factors determine
        how much contamination a specific canned product is likely to contain.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Canned Tomatoes and Tomato Products
      </h3>
      <p>
        Tomato-based products consistently rank among the most contaminated canned foods.
        Tomatoes are highly acidic, with a pH around 4.0, which aggressively interacts
        with can linings. Canned tomato paste, crushed tomatoes, and tomato sauce all
        show elevated levels of both microplastic particles and BPA or BPA-substitute
        leaching. Studies have found that canned tomato products contain significantly
        higher BPA concentrations than nearly any other canned food category.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Canned Soups
      </h3>
      <p>
        Canned soups present a double concern. Many contain acidic tomato-based broths
        that interact with can linings, and they also tend to have high fat content from
        cream or oil bases, which facilitates additional chemical leaching. A Harvard
        School of Public Health study found that people who ate one serving of canned
        soup per day for five days had urinary BPA concentrations more than 1,200%
        higher than those who ate fresh soup.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Canned Tuna and Other Seafood
      </h3>
      <p>
        Canned seafood carries a dual burden. The fish themselves contain microplastics
        accumulated during their lives in increasingly polluted oceans, and the can
        lining adds further contamination. Tuna, sardines, and other oily fish are
        particularly affected because the fat content of the fish facilitates
        chemical extraction from the lining. Additionally, the oil or brine solution
        the fish is packed in provides another medium for microplastic transfer.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Canned Coconut Milk and Cream
      </h3>
      <p>
        High-fat canned products like coconut milk and coconut cream are often
        overlooked but can contain significant contamination. The high fat content
        makes these products particularly effective at extracting chemical additives
        from can linings. Some testing has found elevated BPA levels in canned
        coconut products compared to their fresh or carton-packaged equivalents.
      </p>

      {/* Fresh vs canned comparison */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Fresh vs. Canned: How Big Is the Difference?
      </h2>
      <p>
        The difference in microplastic and chemical exposure between canned and fresh
        food is substantial. Studies comparing urinary BPA levels in people who eat
        primarily fresh food versus primarily canned food consistently find differences
        of 10x or more. Fresh tomatoes contain essentially no microplastics from
        packaging (though they may contain some from agricultural contamination), while
        canned tomatoes are among the most contaminated food products tested.
      </p>
      <p>
        However, the comparison is not entirely straightforward. Fresh produce can be
        contaminated by microplastics from plastic packaging at the store, from soil
        contamination during growing, and from plastic cutting boards and storage
        containers in your kitchen. The advantage of fresh food is not that it is
        microplastic-free, but that it avoids the significant additional contamination
        layer that can linings add.
      </p>
      <p>
        Frozen food offers a middle ground. Frozen vegetables and fruits are typically
        packaged in plastic bags, which do leach some microplastics, but at far lower
        rates than metal can linings because they are not subjected to the extreme
        heat of the canning process. Frozen food generally has lower microplastic
        contamination than canned equivalents, while offering similar convenience
        and shelf stability.
      </p>

      {/* Safer choices */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Safer Alternatives and Smarter Choices
      </h2>
      <p>
        Completely avoiding canned food may not be practical or desirable for everyone.
        Here are strategies to reduce your microplastic exposure while still enjoying
        the convenience that canned foods provide.
      </p>
      <p>
        <strong>Choose glass-jarred alternatives.</strong> Many products that come in
        cans are also available in glass jars. Tomato sauce, pasta sauce, beans, and
        even some soups can be purchased in glass. Glass is inert and does not leach
        any chemicals or microplastics into food.
      </p>
      <p>
        <strong>Cook in inert materials too.</strong> The can is only half the kitchen; what you
        reheat and cook in matters as much. See our{' '}
        <Link href="/blog/best-non-toxic-cookware" className="text-[#1E6B47] hover:underline font-medium">best non-toxic cookware</Link>{' '}
        guide for the lowest-shed pans.
      </p>
      <p>
        <strong>Prefer brands that name their lining.</strong> Most companies will only
        tell you what is <em>not</em> in the can. A few will tell you what is, and that
        is the more useful signal. <strong>Amy&apos;s Kitchen</strong> publishes the most
        specific disclosure of any brand we checked: an acrylic can body and a polyester
        lid, which its FAQ states contains no BPA, BPS, BPF, BADGE or phthalates.{' '}
        <strong>Eden Foods</strong> used a plant-based oleoresin lining from 1999, and
        since 2017 has used an updated BPA- and BPS-free lining that covers high-acid
        foods including tomatoes, so the widely repeated warning that Eden&apos;s tomato
        cans are BPA-lined has been out of date for years. Naming the polymer is not the
        same as proving it safe, but a brand willing to be specific is a brand you can
        actually hold to account.
      </p>
      <p>
        <strong>Prioritize fresh or frozen for high-risk foods.</strong> For tomatoes,
        soups, and coconut milk, the shift to fresh or frozen alternatives has the
        biggest impact on reducing exposure. Use fresh tomatoes for sauces when possible,
        or buy tomatoes in glass jars or cartons.
      </p>
      <p>
        <strong>Rinse canned foods before use.</strong> While this does not remove
        microplastics embedded in the food itself, rinsing canned beans, vegetables,
        and other products under water can reduce some of the surface contamination
        from the liquid they were packed in.
      </p>
      <p>
        <strong>Use the MicroPlastics app to compare products.</strong> The{' '}
        <Link href="/" className="text-[#1E6B47] hover:underline font-medium">
          MicroPlastics app
        </Link>{' '}
        lets you scan product barcodes at the store to compare microplastic risk
        across different brands and packaging types. This makes it easy to identify
        which canned products are safer and when a glass-jarred or fresh alternative
        is available. Checking before you buy takes seconds and can meaningfully
        reduce your household&apos;s cumulative exposure over time.
      </p>

      {/* Conclusion */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Rethinking the Pantry Staple
      </h2>
      <p>
        Canned food has served an important role in global nutrition for over 200
        years. It is affordable, accessible, and reduces food waste through long
        shelf life. But the evidence on microplastic and chemical contamination from
        can linings is now too substantial to ignore. You do not need to eliminate
        canned food entirely, but being strategic about which canned products you
        buy, how often you consume them, and when to choose alternatives can make a
        real difference in your long-term microplastic exposure. As with most aspects
        of microplastic reduction, small, consistent changes in purchasing habits add
        up to meaningful results over time.
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>Packaging material. PET, HDPE, PP, PS, multi-layer, glass, aluminum.</>,
          <>Container condition from the photo, scratches, dents, fade.</>,
          <>Product category, fresh, packaged, canned, frozen, takeout.</>,
          <>Use-context flags you log, microwave, heat, reuse, time stored.</>,
          <>Cited research behind the 0–100 risk score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Use the app as a grocery-store second opinion"
        sub="Scan the product, check the packaging score, compare alternatives. The app weighs material, condition, brand, and the cited research."
        buttonLabel="Scan groceries in the app"
      />


      <BlogFAQ
        items={[
          {
            question: 'Do canned foods contain microplastics?',
            answer:
              'Yes. Every food can has a thin polymer lining (historically a BPA-based epoxy, today usually polyester, acrylic or vinyl) to stop the food touching bare metal. The lining sheds microplastic particles and leaches chemistry into the food, especially during long shelf storage and with acidic contents.',
          },
          {
            question: 'Which canned foods have the most microplastics?',
            answer:
              'Acidic and oily foods leach the most: canned tomatoes, tomato sauce, citrus fruits, pickled vegetables, olives in brine, tuna and salmon in oil. Long shelf storage (over 2 years) amplifies leaching. Low-acid bland foods like canned beans or corn leach less.',
          },
          {
            question: 'Do BPA-free cans just use BPS instead?',
            answer:
              'No: this is a myth, and we used to repeat it here. Studies that actually identify what a can is lined with (the can industry fingerprinted 234 US cans in 2020; the Ecology Center tested 192 in 2016) find polyester, acrylic, vinyl and epoxy, and no BPS or BPF used as a lining. The BPA-to-BPS switch everyone cites really happened in thermal till receipts, not cans. That said, "BPA-free" only means BPA was not added on purpose: trace bisphenols still migrate from non-BPA cans, one major replacement (TMBPF) is itself a bisphenol, and the substitutes have been studied far less than BPA was. Better, but not settled.',
          },
          {
            question: 'Are glass-jarred or pouched alternatives safer than cans?',
            answer:
              'Glass jars are clearly safer for acidic foods, no polymer lining at all. Pouches (Tetrapak, retort pouches) are typically lower-risk than cans for similar foods, though pouches have plastic films. For tomatoes, citrus, pickles, and sauces, the priority is to switch to glass.',
          },
          {
            question: 'Should I avoid canned tuna because of microplastics?',
            answer:
              'Canned tuna combines two sources: microplastics from the fish itself (food chain bioaccumulation) and BPA/microplastic leaching from the can lining. Fresh wild-caught fish in glass containers is cleaner. Pouched tuna is similar risk to canned. For occasional use, canned tuna is acceptable; daily consumption is not recommended.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Hussain KA, Romanova S, Okur I, et al.',
            year: 2023,
            title: 'Assessing the Release of Microplastics and Nanoplastics from Plastic Containers and Reusable Food Pouches',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.3c01942',
          },
          {
            authors: 'Cox KD, Covernton GA, Davies HL, et al.',
            year: 2019,
            title: 'Human Consumption of Microplastics',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.9b01517',
          },
          {
            authors: 'European Food Safety Authority',
            year: 2016,
            title: 'Presence of microplastics and nanoplastics in food, with particular focus on seafood',
            publisher: 'EFSA Journal',
            url: 'https://www.efsa.europa.eu/en/efsajournal/pub/4501',
          },
          {
            authors: 'Karami A, Golieskardi A, Choo CK, et al.',
            year: 2017,
            title: 'The presence of microplastics in commercial salts from different countries',
            publisher: 'Scientific Reports',
            url: 'https://www.nature.com/articles/srep46173',
          },
          {
            authors: 'WHO',
            year: 2022,
            title: 'Dietary and inhalation exposure to nano- and microplastic particles',
            publisher: 'World Health Organization',
            url: 'https://www.who.int/publications/i/item/9789240054608',
          },
          {
            authors: 'Carwile JL, Ye X, Zhou X, Calafat AM, Michels KB',
            year: 2011,
            title: 'Canned Soup Consumption and Urinary Bisphenol A: A Randomized Crossover Trial',
            publisher: 'JAMA',
            url: 'https://jamanetwork.com/journals/jama/fullarticle/201917',
          },
          {
            authors: 'Can Manufacturers Institute',
            year: 2020,
            title: 'Canned Food Market Basket Survey: FTIR lining identification of 234 US cans (filed with Washington Dept. of Ecology)',
            publisher: 'Washington State Department of Ecology',
            url: 'https://www.cancentral.com/innovations-food-cans/',
          },
          {
            authors: 'Ecology Center',
            year: 2016,
            title: 'Buyer Beware: Toxic BPA and Regrettable Substitutes in the Linings of Canned Food',
            publisher: 'Ecology Center',
            url: 'https://www.ecocenter.org/food-can-study-2016-executive-summary',
          },
          {
            authors: 'Rochester JR, Bolden AL',
            year: 2015,
            title: 'Bisphenol S and F: A Systematic Review and Comparison of the Hormonal Activity of Bisphenol A Substitutes',
            publisher: 'Environmental Health Perspectives',
            url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC4492270/',
          },
          {
            authors: 'Zhang Z, Scarsella JB, Hartman TG',
            year: 2020,
            title: 'Identification and Quantitation of TMBPF and Migrants from Non-BPA Can Coatings',
            publisher: 'Polymers',
            url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC7760808/',
          },
          {
            authors: 'Canadian Food Inspection Agency',
            year: 2021,
            title: 'Bisphenol A and BPA alternatives in selected canned foods (402 samples)',
            publisher: 'Government of Canada',
            url: 'https://inspection.canada.ca/en/food-safety-industry/food-chemistry-and-microbiology/food-safety-testing-reports-and-journal-articles/bisphenol-and-bpa-alternatives-selected-canne',
          },
        ]}
      />


    </BlogPostLayout>
  )
}
